import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { CheckCircle2, MailCheck, RefreshCw } from 'lucide-react';
import Navbar from '../Navbar';
import SiteFooter from '../SiteFooter';
import { auth } from '../../firebase/config';

const PRIMARY_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 font-semibold text-white shadow-[0_18px_40px_-22px_rgba(239,68,68,0.7)] transition hover:shadow-[0_22px_44px_-20px_rgba(239,68,68,0.75)] disabled:opacity-50';
const SECONDARY_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-xl border border-orange-200 bg-white px-6 py-4 font-semibold text-orange-700 transition hover:bg-orange-50 disabled:opacity-50';

function getSafeContinuePath(value, fallback = '/') {
  if (!value) {
    return fallback;
  }

  try {
    const parsed = new URL(value, window.location.origin);

    if (parsed.origin !== window.location.origin) {
      return fallback;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}` || fallback;
  } catch (error) {
    return fallback;
  }
}

function mapActionError(error, mode) {
  const code = error?.code || '';

  if (code === 'auth/expired-action-code' || code === 'auth/invalid-action-code') {
    return mode === 'resetPassword'
      ? 'Deze resetlink is ongeldig of verlopen. Vraag een nieuwe aan.'
      : 'Deze verificatielink is ongeldig of verlopen. Vraag een nieuwe aan.';
  }

  if (code === 'auth/user-disabled') {
    return 'Dit account is uitgeschakeld. Neem contact op via hello@swapify.nl.';
  }

  return 'Er ging iets mis bij het verwerken van deze link. Probeer het opnieuw.';
}

const AuthActionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const continuePath = useMemo(
    () => getSafeContinuePath(searchParams.get('continueUrl'), '/'),
    [searchParams],
  );

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const handleAction = async () => {
      if (!mode || !oobCode) {
        setStatus('error');
        setError('Deze link is niet compleet of wordt niet meer ondersteund.');
        return;
      }

      setError('');
      setInfoMessage('');

      try {
        if (mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);

          if (auth.currentUser) {
            await auth.currentUser.reload();
            await auth.currentUser.getIdToken(true);
          }

          if (!cancelled) {
            setStatus('verify-success');
          }
          return;
        }

        if (mode === 'resetPassword') {
          const resolvedEmail = await verifyPasswordResetCode(auth, oobCode);

          if (!cancelled) {
            setEmail(resolvedEmail);
            setStatus('reset-form');
          }
          return;
        }

        if (!cancelled) {
          setStatus('error');
          setError('Deze link wordt nog niet ondersteund.');
        }
      } catch (actionError) {
        if (!cancelled) {
          setStatus('error');
          setError(mapActionError(actionError, mode));
        }
      }
    };

    handleAction();

    return () => {
      cancelled = true;
    };
  }, [mode, oobCode]);

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setInfoMessage('');

    if (password.length < 10) {
      setError('Wachtwoord moet minimaal 10 tekens zijn.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen.');
      return;
    }

    setSubmitting(true);

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('reset-success');
      setPassword('');
      setConfirmPassword('');
      setInfoMessage('Je wachtwoord is bijgewerkt. Je kunt nu opnieuw inloggen.');
    } catch (submitError) {
      setError(mapActionError(submitError, 'resetPassword'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-red-500" />
          <h1 className="text-3xl font-semibold text-gray-900">Even checken…</h1>
          <p className="mt-3 text-gray-600">We verwerken je link.</p>
        </div>
      );
    }

    if (status === 'verify-success') {
      return (
        <div className="text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-orange-50 text-orange-600">
            <MailCheck className="h-7 w-7" />
          </div>
          <h1 className="text-3xl font-semibold text-gray-900">Je e-mailadres is bevestigd</h1>
          <p className="mt-3 text-gray-600">
            Je account is klaar voor gebruik.
          </p>
          <div className="mt-8">
            <button
              type="button"
              onClick={() => navigate(continuePath, { replace: true })}
              className={PRIMARY_BUTTON_CLASS}
            >
              Ga verder
            </button>
          </div>
        </div>
      );
    }

    if (status === 'reset-form') {
      return (
        <div>
          <div className="text-center">
            <h1 className="text-3xl font-semibold text-gray-900">Kies een nieuw wachtwoord</h1>
            <p className="mt-3 text-gray-600">
              {email ? `Voor ${email}` : 'Vul hieronder je nieuwe wachtwoord in.'}
            </p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="mt-8 space-y-4">
            <div>
              <label className="mb-2 block font-semibold text-gray-900">Nieuw wachtwoord</label>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-red-500 focus:outline-none"
                placeholder="Minimaal 10 tekens"
              />
            </div>
            <div>
              <label className="mb-2 block font-semibold text-gray-900">Bevestig wachtwoord</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-red-500 focus:outline-none"
                placeholder="Typ je wachtwoord nog een keer"
              />
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button type="submit" disabled={submitting} className={`w-full ${PRIMARY_BUTTON_CLASS}`}>
              {submitting ? 'Bezig...' : 'Nieuw wachtwoord opslaan'}
            </button>
          </form>
        </div>
      );
    }

    if (status === 'reset-success') {
      return (
        <div className="text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-orange-50 text-orange-600">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h1 className="text-3xl font-semibold text-gray-900">Je wachtwoord is bijgewerkt</h1>
          <p className="mt-3 text-gray-600">
            {infoMessage || 'Je kunt nu opnieuw inloggen.'}
          </p>
          <div className="mt-8">
            <Link to="/login" className={PRIMARY_BUTTON_CLASS}>
              Naar inloggen
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
          <RefreshCw className="h-7 w-7" />
        </div>
        <h1 className="text-3xl font-semibold text-gray-900">Deze link werkt niet meer</h1>
        <p className="mt-3 text-gray-600">{error}</p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link to="/login" className={PRIMARY_BUTTON_CLASS}>
            Naar inloggen
          </Link>
          <Link to="/" className={SECONDARY_BUTTON_CLASS}>
            Terug naar home
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-red-50 via-white to-orange-50">
      <Navbar />
      <main className="flex-1 px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-xl">
          <div className="rounded-[2rem] border border-orange-100 bg-white p-8 shadow-[0_28px_80px_-48px_rgba(234,88,12,0.4)] sm:p-10">
            {renderContent()}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default AuthActionPage;
